import { useState } from "react";
import { FileUp, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { Panel, Th, Td, btn, field } from "@/components/ui-kit";
import { FixButton } from "@/components/sap/FixDialog";

export const gbp = (v) => (v == null ? "-" : `£${Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

export const Header = ({ g }) => (
  <div className="grid gap-x-6 gap-y-1 px-5 py-4 text-sm sm:grid-cols-4" data-testid="amz-gate-header">
    {[["PO", `${g.po_number}${g.version ? ` v${g.version}` : ""}`], ["PO date", g.po_date], ["Delivery", `${g.delivery_date} ${g.delivery_time || ""}`], ["Booking ref", g.booking_ref],
      ["Amazon FC", g.fc], ["SAP ship-to", g.ship_to_sap ? `${g.ship_to_sap} (${g.depot_name})` : "not found"], ["Contact", g.contact], ["File", g.filename]].map(([k, v]) => (
      <div key={k}><span className="font-mono text-[10px] uppercase text-slate-500">{k}</span><div className="text-slate-100" data-testid={`amz-gate-${k.toLowerCase().replace(/\W+/g, "-")}`}>{v || "-"}</div></div>
    ))}
    <SapTexts g={g} />
  </div>
);

const SapTexts = ({ g }) => {
  const slot = [g.delivery_date && `Delivery Date: ${g.delivery_date}`, g.delivery_time && `Delivery Time: ${g.delivery_time}`].filter(Boolean).join(" ");
  const rows = [["Note to Supplier", g.supplier_note], ["Booking-in text (to SAP)", [g.booking_ref && `Booking Ref: ${g.booking_ref}`, slot].filter(Boolean).join("\n")]];
  return rows.map(([k, v]) => (
    <div key={k} className="sm:col-span-2 mt-2">
      <span className="font-mono text-[10px] uppercase text-slate-500">{k}</span>
      <div className="whitespace-pre-line text-slate-100" data-testid={`amz-gate-${k.toLowerCase().replace(/\W+/g, "-")}`}>{v || "-"}</div>
    </div>
  ));
};

export const Lines = ({ g, setPallets }) => (
  <div className="overflow-x-auto">
    <table className="w-full">
      <thead className="border-b border-slate-800"><tr><Th>Ln</Th><Th>Amazon item</Th><Th>SAP</Th><Th>Qty EACH</Th><Th>Per pallet</Th><Th>Pallets</Th><Th>Break price</Th><Th>Amazon price / pallet</Th><Th>Line value</Th></tr></thead>
      <tbody className="divide-y divide-slate-800/70">
        {g.lines.map((l, i) => (
          <tr key={i} data-testid={`amz-line-${l.line_no}`} className={l.flags.length ? "bg-rose-500/10" : ""}>
            <Td>{l.line_no}</Td><Td><div className="font-mono">{l.customer_item}</div><div className="text-xs text-slate-500">{l.description}</div>{l.flags.map((f) => <div key={f} className="text-xs text-rose-300">{f}</div>)}</Td>
            <Td className="font-mono">{l.sap || "-"}</Td><Td className="font-mono">{l.qty_each.toLocaleString("en-GB")}</Td><Td className="font-mono">{l.per_pallet ? l.per_pallet.toLocaleString("en-GB") : "-"}</Td>
            <Td><Input value={l.pallets ?? ""} onChange={(e) => setPallets(i, e.target.value)} className={`h-8 w-20 ${field}`} data-testid={`amz-line-${l.line_no}-pallets`} /></Td>
            <Td className="font-mono">{gbp(l.price)}</Td>
            <Td className={`font-mono ${l.price != null && l.amazon_price_pallet != null && Math.abs(l.price - l.amazon_price_pallet) > 0.01 ? "text-amber-300" : ""}`}>{gbp(l.amazon_price_pallet)}</Td>
            <Td className="font-mono">{gbp((l.pallets || 0) * (l.price || 0))}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const Checks = ({ g, ack, setAck }) => (
  <div className="space-y-2 px-5 py-4 text-sm">
    <div className="flex flex-wrap gap-6 font-mono" data-testid="amz-gate-totals">
      <span>Total pallets: <b data-testid="amz-total-pallets">{g.total_pallets}</b></span><span>Price break: <b data-testid="amz-price-break">{g.price_break || "-"}</b></span>
      <span>Order value: <b data-testid="amz-value">{gbp(g.value)}</b></span><span className="text-slate-500">Amazon PO total: {gbp(g.po_total)}</span>
    </div>
    {g.blocking.map((b) => <div key={b} className="text-rose-300" data-testid="amz-blocking">Stop: {b}</div>)}
    {g.warnings.map((w) => <div key={w} className="text-amber-300" data-testid="amz-warning">Check: {w}</div>)}
    {!g.blocking.length && g.warnings.length > 0 && (
      <label className="flex items-center gap-2 text-slate-300"><Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} data-testid="amz-ack" /> I have checked the points above</label>
    )}
    {!g.blocking.length && !g.warnings.length && <div className="text-green-300" data-testid="amz-all-clear">All checks passed - quantities are whole pallets and prices match Amazon's PO.</div>}
  </div>
);

export const AmazonPoPanel = () => {
  const [g, setG] = useState(null);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stale, setStale] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const upload = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    run(async () => { const { data } = await api.post("/amazon/po-pdf", fd); setG(data); setAck(false); setStale(false); });
  };
  const recheck = () => run(async () => { const { data } = await api.post("/amazon/check", { ...g, lines: g.lines.map(({ flags, ...l }) => l) }); setG(data); setAck(false); setStale(false); });
  const setPallets = (i, v) => setStale(true) || setG({ ...g, lines: g.lines.map((l, j) => (j === i ? { ...l, pallets: v === "" ? "" : Number(v) } : l)) });
  const approve = () => run(async () => {
    const notes = [g.booking_ref && `Booking ref ${g.booking_ref}`, g.delivery_time && `Del time ${g.delivery_time}`, g.contact && `Contact ${g.contact}`].filter(Boolean).join("; ");
    const { data } = await api.post("/manual-orders", { partner: "AMAZON", po_number: g.po_number, po_date: g.po_date, delivery_date: g.delivery_date, depot: g.depot, notes, supplier_note: g.supplier_note || "", booking_ref: g.booking_ref || "", delivery_slot: [g.delivery_date && `Delivery Date: ${g.delivery_date}`, g.delivery_time && `Delivery Time: ${g.delivery_time}`].filter(Boolean).join(" "), source: "Amazon PO PDF", lines: g.lines.map((l) => ({ item: l.part_no, qty: l.pallets })) });
    data.error ? toast.warning(`Order ${data.po_number} saved but SAP IDoc not created: ${data.error}`) : toast.success(`Order ${data.po_number} saved - SAP IDoc ${data.idoc_file} created`);
    setG(null);
  });
  return (
    <Panel title="Amazon PO (PDF) - upload and check" testId="amz-po-panel"
      action={<label className={`inline-flex h-8 cursor-pointer items-center rounded-md px-3 text-xs ${btn.ghost}`} data-testid="amz-upload-label"><FileUp className="mr-1.5 h-3.5 w-3.5" /> Upload Amazon PO PDF<input type="file" accept=".pdf" className="hidden" onChange={upload} data-testid="amz-upload-input" /></label>}>
      {!g ? <p className="px-5 py-4 text-xs text-slate-500" data-testid="amz-empty">Upload a PO PDF downloaded from Amazon. EACH quantities are converted to pallets, the price break is worked out from the total pallets on the PO, and everything is checked before the SAP IDoc is created.</p> : (
        <div data-testid="amz-gate">
          <Header g={g} /><Lines g={g} setPallets={setPallets} /><Checks g={g} ack={ack} setAck={setAck} />
          <div className="flex flex-wrap gap-2 border-t border-slate-800 px-5 py-3">
            <Button variant="ghost" onClick={recheck} disabled={busy} className={btn.ghost} data-testid="amz-recheck-btn"><RefreshCw className="mr-1.5 h-4 w-4" /> Re-check</Button>
            <FixButton fix={g.fix && { ...g.fix, source: `Amazon PO ${g.po_number}` }} onDone={recheck} testId="amz-fix-btn" />
            <Button variant="ghost" onClick={() => { setG(null); setStale(false); }} className={btn.ghost} data-testid="amz-discard-btn">Discard</Button>
            <Button onClick={approve} disabled={busy || stale || g.blocking.length > 0 || (g.warnings.length > 0 && !ack)} className={`ml-auto ${btn.primary}`} data-testid="amz-approve-btn">{stale ? "Re-check first" : "Approve and create SAP IDoc"}</Button>
          </div>
        </div>
      )}
    </Panel>
  );
};
